﻿import {AutocompleteOption} from "../../components/SortFilter/interfaces";
import {useCallback, useMemo} from "react";
import {createFilterOptions, FilterOptionsState} from "@mui/material";

// Single source of truth for the max number of options rendered in any autocomplete.
// Applied by both the strict base filter (via createFilterOptions) and the flexible fallback.
const AUTOCOMPLETE_RESULT_LIMIT = 100;

interface UseAutocompleteOptions {
  autocompleteOptions: AutocompleteOption[];
  value?: string | AutocompleteOption | null;
  filterMode?: 'strict' | 'flexible';
}

/**
 * Shared hook for autocomplete components providing:
 * - Option label handling
 * - Option equality comparison
 * - Configurable filter options (strict by default, flexible opt-in)
 * - Value resolution (converts string ID to option object)
 *
 * @param autocompleteOptions - The list of options to be used in the autocomplete, each with an 'id' and 'label'.
 * @param value - The current value, which can be a string ID or an AutocompleteOption. The hook will resolve it to the correct option object.
 * @param filterMode - Controls how options are matched against user input.
 * - `'strict'` (default): matches options whose label contains the typed input.
 *   e.g. typing "Trucking" matches "HY Trucking"
 * - `'flexible'`: runs strict matching first; if no results are found, falls back
 *   to matching options whose label is contained within the typed input.
 *   e.g. typing "HY Trucking LLC" can still match "HY Trucking"
 *
 */
const useAutocompleteOptions = ({ autocompleteOptions, value, filterMode = 'strict' }: UseAutocompleteOptions) => {
  const handleIsOptionEqualToValue = useCallback((option: AutocompleteOption, val: AutocompleteOption | null) => {
    return option.id === (val?.id ?? '');
  }, []);

  const handleGetOptionLabel = useCallback((option: string | AutocompleteOption): string => {
    return (typeof option === 'string' && autocompleteOptions.find(testOption => testOption.id === option)?.label) || (option as AutocompleteOption)?.label || ''
  }, [autocompleteOptions]);

  // Custom filter options for better typeahead - matches anywhere in the string
  // Limits displayed results to AUTOCOMPLETE_RESULT_LIMIT to prevent performance degradation with large option sets.
  const baseFilterOptions = useMemo(() => createFilterOptions({
    matchFrom: 'any',
    stringify: (option: AutocompleteOption) => option.label || '',
    limit: AUTOCOMPLETE_RESULT_LIMIT,
  }), []);

  const normalizedOptions = useMemo(() => {
    if (filterMode !== 'flexible') {
      return [];
    }

    return autocompleteOptions.map((option) => ({
      option,
      normalizedKeys: [option.label, ...(option.searchAliases ?? [])]
        .map((key) => key?.toLowerCase().trim())
        .filter((key): key is string => !!key),
    }));
  }, [autocompleteOptions, filterMode]);

  /**
   * Wrapper around the MUI {@link createFilterOptions} function to provide flexible filtering behavior.
   * Filtered Options are capped at {@link AUTOCOMPLETE_RESULT_LIMIT} to prevent performance issues with large option sets.
   * In 'strict' mode (default), it returns options whose label contains the typed input.
   *  - "Trucking" matches "HY Trucking"
   *
   * In 'flexible' mode, it first attempts strict matching; if no results are found, it falls back to returning options whose label
   * (or any of its `searchAliases`) is contained within the typed input.
   *  - "HY Trucking LLC" can still match "HY Trucking"
   *  - "HY Trucking LLC" can match "HY Trucking - dispatch@hy.com" when it has the alias "HY Trucking"
   */
  const filterOptions = useCallback((options: AutocompleteOption[], state: FilterOptionsState<AutocompleteOption>) => {
    const strictMatches = baseFilterOptions(options, state);

    // Skip flexible matching if strict matches exist or input is empty/whitespace
    if (filterMode !== 'flexible' || strictMatches.length > 0 || !state.inputValue.trim()) {
      return strictMatches;
    }

    const normalizedInput = state.inputValue.toLowerCase().trim();
    const results: AutocompleteOption[] = [];
    // Complexity: O(n * k) where n = number of options, k = length of input string
    for (const normalizedOption of normalizedOptions) {
      if (normalizedOption.normalizedKeys.some((key) => normalizedInput.includes(key))) {
        results.push(normalizedOption.option);
        if (results.length === AUTOCOMPLETE_RESULT_LIMIT) break;
      }
    }
    return results;
  }, [baseFilterOptions, filterMode, normalizedOptions]);

  // Convert string value (ID) to the matching option object for proper Autocomplete behavior
  // Note: Check for undefined/null explicitly since empty string '' is a valid ID for "All" option
  // Returns undefined instead of null to be compatible with disableClearable Autocompletes
  const resolvedValue = useMemo((): AutocompleteOption | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string') {
      return autocompleteOptions.find(opt => opt.id === value);
    }
    return value;
  }, [value, autocompleteOptions]);

  return {
    handleGetOptionLabel,
    handleIsOptionEqualToValue,
    filterOptions,
    resolvedValue,
  }
}

export { useAutocompleteOptions };
